import React, { useEffect, useRef } from 'react';
import { Coins, Film, LayoutGrid, MoonStar, Sofa, Sparkles } from 'lucide-react';
import {
  HYPERDUSK_DAY_TO_DUSK_CREDIT_COST,
  IMAGEMOTION_TESTER_COMPLETION_COPY,
  PHOTO_CLEAR_CREDIT_COST,
  STAGING_STANDARD_CREDIT_COST,
  imagemotionRemainingCreditsCopy,
} from '@rehub/pricing';
import { navigateToRehubApp, rehubAppUrl, type AppId } from './Header';
import { trackImagemotionEvent, creditBalanceRange } from '../services/analytics';

interface TesterConversionPanelProps {
  userId: string;
  projectId: string;
  userCredits: number;
  onCreateFullVideo: () => void;
}

const SUGGESTED_APPS: { id: AppId; label: string; icon: typeof Sofa; credits: number }[] = [
  { id: 'staging', label: 'Virtual Staging', icon: Sofa, credits: STAGING_STANDARD_CREDIT_COST },
  { id: 'photoclear', label: 'Photoclear', icon: Sparkles, credits: PHOTO_CLEAR_CREDIT_COST },
  { id: 'hyperdusk', label: 'Hyper Dusk', icon: MoonStar, credits: HYPERDUSK_DAY_TO_DUSK_CREDIT_COST },
];

function creditLabel(credits: number): string {
  return `from ${credits} ${credits === 1 ? 'credit' : 'credits'}`;
}

const TesterConversionPanel: React.FC<TesterConversionPanelProps> = ({
  userId,
  projectId,
  userCredits,
  onCreateFullVideo,
}) => {
  const viewedRef = useRef<string | null>(null);

  useEffect(() => {
    if (viewedRef.current === projectId) return;
    viewedRef.current = projectId;
    trackImagemotionEvent('tester_conversion_viewed', userId, {
      package_id: 'tester',
      credit_balance_range: creditBalanceRange(userCredits),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per project
  }, [projectId, userId]);

  const openApp = (e: React.MouseEvent, id: AppId) => {
    e.preventDefault();
    trackImagemotionEvent('rehub_app_suggestion_clicked', userId, {
      source: 'tester_completion',
      app_source: id,
      credit_balance_range: creditBalanceRange(userCredits),
    });
    void navigateToRehubApp(rehubAppUrl(id));
  };

  return (
    <section
      className="rounded-2xl border border-slate-700 bg-slate-900/80 p-5 sm:p-6"
      aria-labelledby="tester-conversion-heading"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-purple-500/15 border border-purple-500/30 flex items-center justify-center shrink-0">
          <Film className="w-5 h-5 text-purple-300" />
        </div>
        <div>
          <h3 id="tester-conversion-heading" className="text-lg sm:text-xl font-bold text-white">
            {IMAGEMOTION_TESTER_COMPLETION_COPY.headline}
          </h3>
          <p className="text-sm text-slate-300 mt-1 leading-relaxed">
            {IMAGEMOTION_TESTER_COMPLETION_COPY.body}
          </p>
        </div>
      </div>

      <div className="mt-5 flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={() => {
            trackImagemotionEvent('tester_full_video_clicked', userId, {
              source: 'tester_completion',
              credit_balance_range: creditBalanceRange(userCredits),
            });
            onCreateFullVideo();
          }}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
        >
          <Film className="w-4 h-4" />
          {IMAGEMOTION_TESTER_COMPLETION_COPY.primaryCta}
        </button>
        <a
          href={rehubAppUrl('hub')}
          onClick={(e) => openApp(e, 'hub')}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
        >
          <LayoutGrid className="w-4 h-4" />
          {IMAGEMOTION_TESTER_COMPLETION_COPY.secondaryCta}
        </a>
      </div>

      <p className="mt-4 inline-flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-300">
        <Coins className="w-4 h-4 text-amber-300 shrink-0" />
        {imagemotionRemainingCreditsCopy(userCredits)}
      </p>

      <ul className="mt-3 flex flex-wrap gap-2" aria-label="Other ReHub AI tools">
        {SUGGESTED_APPS.map((app) => (
          <li key={app.id}>
            <a
              href={rehubAppUrl(app.id)}
              onClick={(e) => openApp(e, app.id)}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 bg-slate-800/60 px-3 py-1.5 text-xs text-slate-300 hover:border-slate-500 hover:text-white transition-colors"
            >
              <app.icon className="w-3.5 h-3.5 text-sky-300" />
              <span className="font-medium">{app.label}</span>
              <span className="text-slate-500">· {creditLabel(app.credits)}</span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default TesterConversionPanel;
